import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { STATUS_CHIP_STYLES, statusLabel } from '@/utils/surveyStatus';
import { deleteSurvey, listSurveys } from '@/db/localDatabase';
import { processQueue } from '@/services/syncService';
import { useConfirm } from '@/hooks/useConfirm';
import { useSyncState } from '@/hooks/useSyncState';
import type { Survey } from '@/types/survey';
import { totalCount } from '@/utils/counts';
import { elapsedSeconds, formatDurationShort } from '@/utils/duration';
import { formatClockTime, formatDisplayDate, formatNumber } from '@/utils/format';

/** Surveys recorded on this device. Read-only — counts cannot be edited here. */
export function HistoryPage() {
  const navigate = useNavigate();
  const { pending, confirm, answer } = useConfirm();
  const syncState = useSyncState();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setSurveys(await listSurveys());
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, syncState.lastRunAt]);

  const handleDelete = async (survey: Survey) => {
    const ok = await confirm({
      title: 'Delete this survey from the phone?',
      body: `${survey.id}\n\nIt has already been saved to the database, so deleting it here only frees space on this device.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteSurvey(survey.id);
      setError(null);
      await reload();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete the survey.');
    }
  };

  return (
    <>
      <AppHeader
        title="Local History"
        subtitle="Surveys stored on this device"
        showHistoryLink={false}
      />

      <div className="page-body space-y-4">
        {error ? (
          <div
            className="rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-900"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {syncState.pending > 0 ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border-2 border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              {syncState.pending} delivery{syncState.pending === 1 ? '' : 's'} still waiting to
              upload.
            </p>
            <button
              type="button"
              className="btn-secondary shrink-0 px-3 py-2"
              onClick={() => void processQueue({ force: true })}
              disabled={syncState.running}
            >
              {syncState.running ? 'Syncing…' : 'Retry Sync'}
            </button>
          </div>
        ) : null}

        {loading ? <p className="text-slate-500">Loading…</p> : null}

        {!loading && surveys.length === 0 ? (
          <p className="text-slate-500">No surveys have been recorded on this device yet.</p>
        ) : null}

        <ul className="space-y-2">
          {surveys.map((survey) => {
            const duration = elapsedSeconds(survey.actualStartTimestamp, survey.actualEndTimestamp);
            const unfinished =
              survey.status === 'draft' ||
              survey.status === 'ready' ||
              survey.status === 'recording';
            return (
              <li key={survey.id} className="card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">
                      {formatDisplayDate(survey.surveyDate)} · {survey.roadNumber || '—'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {formatClockTime(survey.actualStartTimestamp)} –{' '}
                      {formatClockTime(survey.actualEndTimestamp)}
                      {duration > 0 ? ` · ${formatDurationShort(duration)}` : ''}
                    </p>
                    <p className="mt-1 text-sm text-slate-700">
                      {formatNumber(totalCount(survey.vehicleCounts))} vehicles ·{' '}
                      {survey.district || '—'}
                    </p>
                    <p className="mt-1 font-mono text-xs text-slate-400">{survey.id}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
                      STATUS_CHIP_STYLES[survey.status]
                    }`}
                  >
                    {statusLabel(survey.status)}
                  </span>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary flex-1 py-2"
                    onClick={() =>
                      navigate(unfinished ? `/count/${survey.id}` : `/summary/${survey.id}`)
                    }
                  >
                    {unfinished ? 'Resume' : 'Open Summary'}
                  </button>
                  {survey.databaseSynced ? (
                    <button
                      type="button"
                      className="btn-secondary px-3 py-2 text-red-700"
                      onClick={() => handleDelete(survey)}
                      aria-label={`Delete survey ${survey.id} from this device`}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="sticky-footer">
        <Link to="/" className="btn-primary w-full">
          New Survey
        </Link>
      </div>

      <ConfirmDialog request={pending} onAnswer={answer} />
    </>
  );
}
